/**
 * The "Next / closed" cell, shared by the `/pr` and `/inbox` pipeline tables.
 *
 * One concept, two boards: a live row says what happens next and when; a closed
 * row says when it closed and why — never both, because a closed matter has no
 * next action and a live one has no closing reason. It lives in its own module
 * rather than inside either board's lib because both tables render this exact
 * column, and a second copy of the ternary is precisely how the two boards
 * would drift apart.
 */

export interface PipelineOutcome {
  /** Cell prefix — "Next" while the row is live, "Closed" once it is not. */
  readonly label: "Next" | "Closed";
  readonly at: string | null;
  readonly note: string | null;
  /** Nothing to render — the cell shows an em dash rather than a bare label. */
  readonly empty: boolean;
}

/**
 * `next_action` is the inbox side's free-text "what I owe them"; PR engagements
 * carry only a date, so `/pr` passes `null` and the note line drops out.
 */
export function pipelineOutcome(row: {
  readonly stage: string;
  readonly closed_at: string | null;
  readonly closed_reason: string | null;
  readonly next_action_at: string | null;
  readonly next_action: string | null;
}): PipelineOutcome {
  const closed = row.stage === "closed";
  const at = closed ? row.closed_at : row.next_action_at;
  const note = closed ? row.closed_reason : row.next_action;
  return {
    label: closed ? "Closed" : "Next",
    at,
    note,
    empty: at === null && note === null,
  };
}
